import { ServiceType } from '../types'

// API Response Types
export interface ServiceConfig {
  id: ServiceType
  name: string
  description: string
  shortDescription: string
  baseUrl: string
  pricing: string
  ctaText: string
  icon: string
  features: string[]
  isActive: boolean
  isComingSoon?: boolean
  accentColor: string
  status: 'active' | 'inactive' | 'error'
}

export interface HealthCheckResponse {
  status: 'healthy' | 'unhealthy'
  services: {
    [serviceId: string]: {
      status: 'active' | 'inactive' | 'error'
      responseTime?: number
      lastCheck: string
      error?: string
    }
  }
  timestamp: string
}

export interface ServiceListResponse {
  services: ServiceConfig[]
  total: number
  environment: string
}

// API Configuration
import { getApiBaseUrl } from '../config/services'
const API_BASE_URL = getApiBaseUrl()
const API_TIMEOUT = 5000 // 5 seconds

// API Error Types
export class APIError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string
  ) {
    super(message)
    this.name = 'APIError'
  }
}

// HTTP Client with timeout and error handling
class HTTPClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT)
    const fullUrl = `${API_BASE_URL}${endpoint}`

    console.log(`🌐 Making ${options.method || 'GET'} request to:`, fullUrl)
    console.log('🔧 Request options:', { ...options, signal: undefined })

    try {
      const response = await fetch(fullUrl, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      })

      clearTimeout(timeoutId)
      console.log(`📡 Response status: ${response.status} ${response.statusText}`)

      if (!response.ok) {
        console.error(`❌ HTTP Error: ${response.status} ${response.statusText}`)
        throw new APIError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status
        )
      }

      const data = await response.json()
      console.log('📦 Response data:', data)
      return data
    } catch (error) {
      clearTimeout(timeoutId)
      console.error('❌ Request failed:', error)
      
      if (error instanceof APIError) {
        throw error
      }
      
      if (error instanceof Error && error.name === 'AbortError') {
        console.error('⏰ Request timeout')
        throw new APIError('Request timeout', 408, 'TIMEOUT')
      }
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('🌐 Network error - API server unavailable')
        throw new APIError('Network error - API server unavailable', 0, 'NETWORK_ERROR')
      }
      
      console.error('❓ Unknown error occurred')
      throw new APIError('Unknown error occurred', 500, 'UNKNOWN_ERROR')
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }
}

// API Service Class
class APIService {
  private client = new HTTPClient()

  /**
   * Check API health and connectivity
   */
  async healthCheck(): Promise<HealthCheckResponse> {
    try {
      return await this.client.get<HealthCheckResponse>('/health')
    } catch (error) {
      console.error('Health check failed:', error)
      throw error
    }
  }

  /**
   * Get list of all services with their configuration
   */
  async getServices(): Promise<ServiceListResponse> {
    try {
      return await this.client.get<ServiceListResponse>('/services')
    } catch (error) {
      console.error('Failed to fetch services:', error)
      throw error
    }
  }

  /**
   * Get configuration for a specific service
   */
  async getService(serviceId: ServiceType): Promise<ServiceConfig> {
    try {
      return await this.client.get<ServiceConfig>(`/services/${serviceId}`)
    } catch (error) {
      console.error(`Failed to fetch service ${serviceId}:`, error)
      throw error
    }
  }

  /**
   * Get service health status
   */
  async getServiceHealth(serviceId: ServiceType): Promise<{
    status: 'active' | 'inactive' | 'error'
    responseTime?: number
    lastCheck: string
    error?: string
  }> {
    try {
      return await this.client.get(`/services/${serviceId}/health`)
    } catch (error) {
      console.error(`Failed to fetch health for service ${serviceId}:`, error)
      throw error
    }
  }

  /**
   * Check if API is available
   */
  async isAPIAvailable(): Promise<boolean> {
    try {
      await this.healthCheck()
      return true
    } catch (error) {
      return false
    }
  }

  /**
   * Get fallback services configuration
   */
  getFallbackServices(): ServiceConfig[] {
    // Import here to avoid circular dependency
    const { getAllServiceConfigs } = require('../config/services')
    return getAllServiceConfigs()
  }
}

// Export singleton instance
export const apiService = new APIService()

// Types are exported above where they are defined